import React, { useEffect, useState } from 'react';
import {
    Grid,
    Typography,
    Card,
    CardContent,
    Divider,
    Button,
    Box,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    useTheme
} from '@mui/material';
import { Add, Delete, Edit, EditOutlined, Groups, Remove, RemoveCircle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { getDashboard } from 'apis/controllers/club';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleIcon from '@mui/icons-material/People';
import SportsIcon from '@mui/icons-material/Sports';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import ClubTotalAmountCard from './TotalAmountCard';
import ClubProductModal from './ClubProductModal';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import ClubPaymentModal from './ClubPaymentModal';
import ClubPaymentStatusChip from './ClubPaymentStatusChip';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Backdrop } from '@mui/material';
import { club as clubApi } from 'apis';

const ClubDashboard = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [club, setClub] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);
    const [clubModalOpen, setClubModalOpen] = useState(false);
    const [totalAmount, setTotalAmount] = useState({ amounts: [], totalAmount: 0 });
    const [simpleDialogOpen, setSimpleDialogOpen] = React.useState(false);
    const [products, setProducts] = useState([]);
    const [chips, setChips] = useState([]);
    const productForm = useForm();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();

    useEffect(() => {
        const fetchClub = async () => {
            try {
                const res = await getDashboard();
                let selProd;
                res?.data?.products?.forEach((product) => {
                    if (product?.selected) {
                        selProd = product?.id;
                        const discount = product?.discount || 0;
                        const finalAmount = product.unitPrice - discount;
                        setTotalAmount({
                            amounts: [{ id: product?.id, label: product?.label, amount: finalAmount }],
                            totalAmount: finalAmount
                        });
                    }
                });
                setClub(res?.data?.club);
                setSelectedProduct(selProd);
                setProducts(res?.data?.products);
                setChips(res?.data?.selectedProducts);
                setStats(res?.data?.stats);
            } catch (e) {
                console.error(e);
                setError('Error loading club dashboard.');
            } finally {
                setLoading(false);
            }
        };
        fetchClub();
    }, []);

    if (error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    const handleEditClub = () => {
        navigate(`/clubs/manage/${club?.id}/details`);
    };

    const handleClubRenewal = async (productTypeId, amount, label, discountCode = null, comments = null, discount = 0) => {
        try {
            setLoading(true);

            /* Uncomment below, import apis and do your things <3 */

            const res = await clubApi.createOrUpdateClubOrder({
                Action: 'add',
                ProductTypeId: productTypeId,
                DiscountCode: discountCode,
                Comments: comments
            });

            setLoading(false);

            /* Uncomment below as well <3 */

            enqueueSnackbar(t(res?.message || 'Success'), { variant: res?.status });

            if (res?.status !== 'success') return;

            // Update Total Amount
            setTotalAmount((prev) => {
                const updatedAmounts = [...prev.amounts];
                const existing = updatedAmounts.find((a) => a.id === productTypeId);

                const finalAmount = discount > 0 ? amount - amount * discount : amount;

                if (existing) {
                    existing.amount = finalAmount;
                } else {
                    updatedAmounts.push({
                        label,
                        amount: finalAmount,
                        id: productTypeId
                    });
                }
                setSelectedProduct(productTypeId);
                const total = updatedAmounts.reduce((sum, a) => sum + a.amount, 0);
                return {
                    amounts: updatedAmounts,
                    totalAmount: total
                };
            });
            setClubModalOpen(false);
            setChips((prev) => {
                if (prev.some((chip) => chip.id === productTypeId)) return prev;
                return [...prev, { id: productTypeId, label, expiresAt: null, paid: false }]; // Adjust `status` as needed
            });
            setProducts((prevProducts) =>
                prevProducts.map((product) =>
                    product.id === productTypeId
                        ? {
                              ...product,
                              discount,
                              discountCode,
                              comments,
                              selected: true
                          }
                        : {
                              ...product,
                              selected: false
                          }
                )
            );
        } catch (error) {
            setLoading(false);
            console.error(error);
            enqueueSnackbar(t('ErrorFound'), { variant: 'error' });
        }
    };

    const handleClubRemoveProduct = async (productTypeId) => {
        try {
            setLoading(true);

            const res = await clubApi.createOrUpdateClubOrder({
                Action: 'delete',
                ProductTypeId: productTypeId
            });

            setLoading(false);
            enqueueSnackbar(t(res?.message || 'Deleted'), { variant: res?.status });

            if (res?.status !== 'success') return;

            // Update Total Amount
            setProducts((prev) =>
                prev.map((p) =>
                    p.id === productTypeId ? { ...p, selected: false, discount: null, discountCode: null, comments: null } : p
                )
            );
            setSelectedProduct(null);
            setChips((prev) => prev.filter((chip) => chip.id !== productTypeId));
            // Update Total Amount
            setTotalAmount((prev) => {
                const updatedAmounts = prev.amounts.filter((entry) => entry.id !== productTypeId);
                const total = updatedAmounts.reduce((sum, entry) => sum + entry.amount, 0);
                return {
                    amounts: updatedAmounts,
                    totalAmount: total
                };
            });
        } catch (e) {
            setLoading(false);
            console.error(e);
            enqueueSnackbar(t('ErrorFound'), { variant: 'error' });
        }
    };
    const fullSelectedProduct = products?.find((p) => p.id === selectedProduct);

    const tileStyle = {
        cursor: 'pointer',
        backgroundColor: 'aliceblue',
        p: 2,
        borderRadius: 2,
        minHeight: 120,
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 6px 16px rgba(0, 123, 255, 0.2)'
        }
    };

    return (
        <Box sx={{ position: 'relative' }}>
            <Backdrop
                open={loading}
                sx={{
                    zIndex: (theme) => theme.zIndex.modal + 1,
                    color: '#fff'
                }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <Box sx={{ p: 4 }}>
                <FormProvider {...productForm}>
                    <ClubProductModal
                        open={clubModalOpen}
                        onSubmit={handleClubRenewal}
                        onClose={() => setClubModalOpen(false)}
                        products={products || []}
                    />
                </FormProvider>
                <ClubPaymentModal
                    open={simpleDialogOpen}
                    handleClose={() => setSimpleDialogOpen(false)}
                    totalAmount={totalAmount?.totalAmount}
                    selectedProduct={selectedProduct}
                />

                <Grid container spacing={3}>
                    {/* Top Row */}
                    <Grid item container spacing={3}>
                        {/* Club Info + Stats */}
                        <Grid item xs={12} md={8}>
                            <Card elevation={4} sx={{ borderRadius: 3, p: 3 }}>
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Box display="flex" alignItems="center">
                                            {club?.avatar ? (
                                                <img
                                                    src={club.avatar}
                                                    alt="Club Avatar"
                                                    style={{ width: 120, height: 120, borderRadius: 20, marginRight: 20 }}
                                                />
                                            ) : (
                                                <Groups sx={{ fontSize: 100, color: 'primary.main', mr: 2 }} />
                                            )}
                                            <Box>
                                                <Typography variant="h3" fontWeight="bold">
                                                    {club?.fullName}{' '}
                                                    <Typography component="span" variant="h5" color="text.secondary">
                                                        ({club?.abbr}) ({club?.code})
                                                    </Typography>
                                                </Typography>
                                                <Typography fontWeight="bold" color="text.secondary">
                                                    {club?.type}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Tooltip title={t('EditClub')}>
                                            <IconButton sx={{ bgcolor: '#E3F2FD' }} color="primary" onClick={handleEditClub}>
                                                <EditOutlined />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>

                                    {/* Address */}
                                    <Box mt={2}>
                                        <Typography color="primary" variant="subtitle1" fontWeight="bold">
                                            {t('Address')}
                                        </Typography>
                                        <Box mt={0.5}>
                                            {club?.address1 && (
                                                <Typography>
                                                    {club?.streetNr} {club.address1}
                                                </Typography>
                                            )}
                                            {club?.address2 && <Typography>{club.address2}</Typography>}
                                            {(club?.city || club?.region || club?.zipCode || club?.country) && (
                                                <Typography>
                                                    {[club.zipCode, club.city, club.region, club.country].filter(Boolean).join(', ')}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Stat Tiles */}
                                    <Grid container spacing={2} mt={3}>
                                        <Grid item xs={6} sm={3}>
                                            <Box onClick={() => navigate('/athletes')} sx={tileStyle}>
                                                <PeopleIcon color="primary" fontSize="large" />
                                                <Typography variant="h5">{stats?.numAthletes || 0}</Typography>
                                                <Typography variant="body2">{t('Athletes')}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Box onClick={() => navigate('/officials')} sx={tileStyle}>
                                                <PeopleIcon color="primary" fontSize="large" />
                                                <Typography variant="h5">{stats?.numMembers || 0}</Typography>
                                                <Typography variant="body2">{t('Members')}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Box onClick={() => navigate('/officials?filters=showAll')} sx={tileStyle}>
                                                <SupervisorAccountIcon color="primary" fontSize="large" />
                                                <Typography variant="h5">{stats?.numOfficials || 0}</Typography>
                                                <Typography variant="body2">{t('Officials')}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} sm={3}>
                                            <Box onClick={() => navigate('/officials?filters=Function%3ACoach')} sx={tileStyle}>
                                                <SupervisorAccountIcon color="primary" fontSize="large" />
                                                <Typography variant="h5">{stats?.numCoaches || 0}</Typography>
                                                <Typography variant="body2">{t('Coaches')}</Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Total Amount */}
                        {club?.canPurchase && (
                            <Grid item xs={12} md={4}>
                                <ClubTotalAmountCard
                                    totalAmountData={totalAmount}
                                    paymentButton={
                                        <Button
                                            onClick={() => setSimpleDialogOpen(true)}
                                            variant="contained"
                                            color="success"
                                            fullWidth
                                            sx={{ mt: 2 }}
                                        >
                                            {t('ProceedToPayment')}
                                        </Button>
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>

                    {/* Bottom Row */}
                    {club?.canPurchase && (
                        <Grid item xs={12} md={6}>
                            <Card elevation={3} sx={{ borderRadius: 3 }}>
                                <CardContent>
                                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                                        {t('Renewals')}
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />

                                    {chips.length > 0 && (
                                        <>
                                            {chips.map((chip) => (
                                                <ClubPaymentStatusChip key={chip.id} {...chip} />
                                            ))}
                                        </>
                                    )}

                                    {selectedProduct ? (
                                        <Box display="flex" alignItems="center">
                                            {(() => {
                                                const discounted = totalAmount.amounts.find((a) => a.id === selectedProduct);
                                                const original = fullSelectedProduct?.unitPrice;
                                                const final = discounted?.amount;

                                                return (
                                                    <Typography variant="h4" mr={2} mt={2}>
                                                        {fullSelectedProduct?.label} -{' '}
                                                        {original !== final && final ? (
                                                            <>
                                                                <span
                                                                    style={{
                                                                        textDecoration: 'line-through',
                                                                        color: 'darkred',
                                                                        marginRight: 8
                                                                    }}
                                                                >
                                                                    ${original.toFixed(2)}
                                                                </span>
                                                                <span style={{ color: 'green', fontWeight: 600 }}>
                                                                    ${final.toFixed(2)} CAD
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <span style={{ color: 'green' }}>${original?.toFixed(2)} CAD</span>
                                                        )}
                                                    </Typography>
                                                );
                                            })()}
                                            <Button
                                                variant="contained"
                                                color="error"
                                                onClick={() => handleClubRemoveProduct(selectedProduct)}
                                                sx={{ mt: 2 }}
                                            >
                                                {t('Remove')}
                                            </Button>
                                        </Box>
                                    ) : (
                                        products &&
                                        !products.every((p) => p.paid) && (
                                            <>
                                                <Button
                                                    startIcon={<Add />}
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => setClubModalOpen(true)}
                                                    sx={{ mt: 2 }}
                                                >
                                                    {t('RenewClubMembership')}
                                                </Button>
                                            </>
                                        )
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </Box>
    );
};

export default ClubDashboard;
